/**
 * Cursor feedback: the canvas CSS cursor follows what's under it (WoW-style).
 *  - crosshair while a ground spell is being placed (castux `isPlacing`, highest priority)
 *  - sword over a living hostile in range of the hero's main attack, a greyed sword out of range
 *  - hand over breakables / caches / nodes, a loot bag over ground pickups
 *  - the default pointer otherwise
 * Never touched while pointer lock is active (mouselook hides the cursor). `cursorMode` is pure
 * (unit-tested); `CursorFeedback` only writes `style.cursor` when the mode changes.
 */
import { isPlacing } from './castux';
import { BIND_KEYS, type ContentRegistry, type DeepReadonly, type SimQueries, type UnitId, type Vec2, type World } from '@keystone/sim';

export type CursorMode = 'default' | 'attack' | 'attack-oor' | 'interact' | 'loot' | 'crosshair';

export interface CursorInput {
  /** A ground-targeted spell is being placed. */
  placing: boolean;
  /** Unit under the cursor (null: none). */
  unit: { hostile: boolean; alive: boolean; inRange: boolean } | null;
  /** World object / pickup under the cursor. */
  object: 'interact' | 'loot' | null;
}

export function cursorMode(i: CursorInput): CursorMode {
  if (i.placing) return 'crosshair';
  if (i.unit?.alive && i.unit.hostile) return i.unit.inRange ? 'attack' : 'attack-oor';
  if (i.object) return i.object;
  return 'default';
}

const BASE = `${import.meta.env?.BASE_URL ?? '/'}assets/ui/cursors/`;
/** CSS cursor per mode (hotspots: the sword tip, the fingertip, the arrow tip). */
export const CURSOR_CSS: Record<CursorMode, string> = {
  default: '',
  attack: `url("${BASE}attack.svg") 2 2, crosshair`,
  'attack-oor': `url("${BASE}attack-oor.svg") 2 2, not-allowed`,
  interact: `url("${BASE}interact.svg") 7 3, pointer`,
  loot: `url("${BASE}loot.svg") 3 2, pointer`,
  crosshair: 'crosshair',
};

/** Pickups count as under the cursor within this many yards of the ground point. */
const PICKUP_PICK_R = 0.9;

/** The world object / pickup at a ground point (sim yards), if any. */
export function objectAt(world: DeepReadonly<World>, p: Vec2 | null): 'interact' | 'loot' | null {
  if (!p) return null;
  for (const k of world.pickups ?? []) if (Math.hypot(k.pos.x - p.x, k.pos.y - p.y) < PICKUP_PICK_R) return 'loot';
  for (const o of world.worldObjects ?? []) {
    if (o.broken || (o.hidden && !o.discovered)) continue;
    if (Math.hypot(o.pos.x - p.x, o.pos.y - p.y) < o.radius + 0.3) return o.class === 'cache' ? 'loot' : 'interact';
  }
  return null;
}

/** Shared flag for code without a CursorFeedback handle (e.g. castux ground placement): true while placing. */
export const cursorSignals = { placing: false };

export class CursorFeedback {
  private mode: CursorMode = 'default';
  private readonly rangeOf = new Map<string, number>();
  /** Ground placement probe (castux); set by the session when that feature exists. */
  isPlacing: (() => boolean) | null = null;

  constructor(
    private readonly surface: HTMLElement,
    private readonly content: ContentRegistry,
    private readonly queries: SimQueries,
  ) {}

  /** Range (yd) of the class's main enemy-targeted ability (0 = melee). */
  private attackRange(classId: string): number {
    let r = this.rangeOf.get(classId);
    if (r === undefined) {
      r = 0;
      const cls = this.content.class(classId);
      for (const key of BIND_KEYS) {
        const bound = cls?.binds[key];
        const ab = bound ? this.content.ability(bound) : undefined;
        if (ab && ab.targeting === 'enemy') {
          r = ab.range ?? 0;
          break;
        }
      }
      this.rangeOf.set(classId, r);
    }
    return r;
  }

  /** Per frame, after picking. `hoverId`: unit under the cursor; `ground`: ground point under it. */
  update(world: DeepReadonly<World>, playerId: UnitId | null, hoverId: UnitId | null, ground: Vec2 | null): void {
    if (typeof document !== 'undefined' && document.pointerLockElement) return;
    const me = playerId ? world.units[playerId] : undefined;
    const h = hoverId ? world.units[hoverId] : undefined;
    let unit: CursorInput['unit'] = null;
    if (h) {
      const hostile = h.faction !== (me?.faction ?? 'party');
      let inRange = true;
      if (hostile && h.alive && me) {
        try {
          inRange = this.queries.spatial.inRange(world as World, me.id, h.id, me.classId ? this.attackRange(me.classId) : 0);
        } catch {
          inRange = true;
        }
      }
      unit = { hostile, alive: h.alive, inRange };
    }
    let placing = false;
    try {
      placing = this.isPlacing ? this.isPlacing() : isPlacing() || cursorSignals.placing;
    } catch {
      placing = false;
    }
    this.set(cursorMode({ placing, unit, object: unit ? null : objectAt(world, ground) }));
  }

  private set(mode: CursorMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.surface.style.cursor = CURSOR_CSS[mode];
  }

  dispose(): void {
    this.surface.style.cursor = '';
  }
}
